import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Card,
  CardMedia,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add,
  Delete,
  PhotoCamera,
  Description,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { courseAPI, studentAPI, branchAPI, batchAPI, studentPaymentAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

// Kerala districts
const KERALA_DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
  'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasargod'
];

const StudentCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [emailError, setEmailError] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const [paymentData, setPaymentData] = useState({
    paymentType: '',
    amount: '',
    transferDate: today,
    referenceNumber: '',
    fromBank: '',
  });
  const [paymentError, setPaymentError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Course
    course: '',
    batch: '',
    bssFormId: '',

    // Personal Info
    nameAsPerTC: '',
    dateOfBirth: '',
    gender: '',
    phone: '',
    email: '',

    // Permanent Address
    permanentStreet: '',
    permanentCity: '',
    permanentDistrict: '',
    permanentState: 'Kerala',
    permanentPincode: '',

    // Communication Address
    communicationStreet: '',
    communicationCity: '',
    communicationDistrict: '',
    communicationState: 'Kerala',
    communicationPincode: '',

    // Father/Guardian
    fatherName: '',
    fatherOccupation: '',
    fatherAddress: '',
    fatherPhone: '',

    // Employment
    isEmployed: false,
    employerName: '',
    designation: '',
    employerAddress: '',

    // Declarations
    applicantDeclarationAccepted: false,
    applicantDeclarationPlace: '',
    applicantDeclarationDate: new Date().toISOString().split('T')[0],
    parentDeclarationAccepted: false,
    parentDeclarationPlace: '',
    parentDeclarationDate: new Date().toISOString().split('T')[0],
  });

  // Educational Qualifications (Optional - starts empty)
  const [qualifications, setQualifications] = useState([]);

  useEffect(() => {
    fetchCourses();
    fetchEnabledBatches();
  }, []);

  const fetchEnabledBatches = async () => {
    try {
      const res = await batchAPI.getEnabled();
      setBatches(res.data.data?.batches || []);
    } catch {
      console.error('Failed to load batches');
    }
  };

  const fetchCourses = async () => {
    try {
      const branchId = user?.branch?._id || user?.branch;
      if (branchId) {
        const response = await branchAPI.getOne(branchId);
        const branchData = response.data.data || response.data;
        const branch = branchData.branch || branchData;
        const branchCourses = branch.courses || [];
        console.log('[StudentCreate] branchData:', branchData, 'courses:', branchCourses);
        setCourses(branchCourses.filter(c => c.isActive !== false));
      } else {
        // superadmin fallback — show all active courses
        const response = await courseAPI.getAll();
        setCourses((response.data.data || []).filter(c => c.isActive));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSameAsPermanentChange = (e) => {
    const checked = e.target.checked;
    setSameAsPermanent(checked);

    if (checked) {
      setFormData((prev) => ({
        ...prev,
        communicationStreet: prev.permanentStreet,
        communicationCity: prev.permanentCity,
        communicationDistrict: prev.permanentDistrict,
        communicationState: prev.permanentState,
        communicationPincode: prev.permanentPincode,
      }));
    }
  };

  const handleQualificationChange = (index, field, value) => {
    const updatedQualifications = [...qualifications];
    updatedQualifications[index][field] = value;
    setQualifications(updatedQualifications);
  };

  const addQualification = () => {
    setQualifications([
      ...qualifications,
      {
        examination: '',
        universityOrBoard: '',
        yearOfPassing: '',
        classOrDivision: '',
        gradeOrPercentage: '',
      },
    ]);
  };

  const removeQualification = (index) => {
    if (qualifications.length > 1) {
      const updatedQualifications = qualifications.filter((_, i) => i !== index);
      setQualifications(updatedQualifications);
    } else {
      toast.error('At least one educational qualification is required');
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_DIM = 1200;
          let { width, height } = img;
          if (width > MAX_DIM || height > MAX_DIM) {
            if (width > height) { height = Math.round((height * MAX_DIM) / width); width = MAX_DIM; }
            else { width = Math.round((width * MAX_DIM) / height); height = MAX_DIM; }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            const compressed = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
            resolve(compressed);
          }, 'image/jpeg', 0.85);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size should be less than 10MB');
        return;
      }
      const compressed = await compressImage(file);
      setPhotoFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(compressed);
    }
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (images and PDF)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select an image or PDF file');
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Certificate size should be less than 10MB');
        return;
      }

      setCertificateFile(file);

      // Preview (only for images)
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setCertificatePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setCertificatePreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.course || !formData.batch || !formData.bssFormId.trim() || !formData.nameAsPerTC || !formData.dateOfBirth || !formData.gender || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailError('Please enter a valid email address');
      toast.error('Please enter a valid email address');
      return;
    }

    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (!certificateFile) {
      toast.error('Certificate upload is mandatory');
      return;
    }

    if (!formData.applicantDeclarationAccepted || !formData.parentDeclarationAccepted) {
      toast.error('Please accept both declarations to proceed');
      return;
    }

    // Payment validation
    if (!paymentData.paymentType) {
      setPaymentError('Please select a payment type.');
      toast.error('Payment details are required before submitting.');
      document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (!paymentData.amount || Number(paymentData.amount) <= 0) {
      setPaymentError('Please enter a valid payment amount.');
      toast.error('Please enter a valid payment amount.');
      document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (!paymentData.transferDate) {
      setPaymentError('Please enter the transfer/payment date.');
      toast.error('Please enter the transfer/payment date.');
      return;
    }
    if (paymentData.paymentType !== 'cash' && !paymentData.referenceNumber.trim()) {
      setPaymentError('Reference / transaction number is required for this payment type.');
      toast.error('Reference / transaction number is required.');
      document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (paymentData.paymentType === 'bank_transfer' && !paymentData.fromBank.trim()) {
      setPaymentError('From bank name is required for bank transfers.');
      toast.error('From bank name is required for bank transfers.');
      document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setPaymentError('');

    // Validate qualifications only if any exist
    if (qualifications.length > 0) {
      const incompleteQualification = qualifications.some(q =>
        !q.examination || !q.universityOrBoard || !q.yearOfPassing || !q.classOrDivision || !q.gradeOrPercentage
      );
      if (incompleteQualification) {
        toast.error('Please complete all educational qualification fields or remove incomplete entries');
        return;
      }
    }

    try {
      setLoading(true);

      // Create FormData for file upload
      const submitData = new FormData();

      // Course
      submitData.append('course', formData.course);
      submitData.append('batch', formData.batch);
      submitData.append('bssFormId', formData.bssFormId.trim());

      // Personal Info
      submitData.append('personalInfo', JSON.stringify({
        nameAsPerTC: formData.nameAsPerTC,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        phone: formData.phone,
        email: formData.email,
      }));

      // Permanent Address
      submitData.append('permanentAddress', JSON.stringify({
        street: formData.permanentStreet,
        city: formData.permanentCity,
        district: formData.permanentDistrict,
        state: formData.permanentState,
        pincode: formData.permanentPincode,
      }));

      // Communication Address
      submitData.append('communicationAddress', JSON.stringify({
        street: formData.communicationStreet,
        city: formData.communicationCity,
        district: formData.communicationDistrict,
        state: formData.communicationState,
        pincode: formData.communicationPincode,
      }));

      // Father/Guardian
      submitData.append('fatherOrGuardian', JSON.stringify({
        name: formData.fatherName,
        occupation: formData.fatherOccupation,
        address: formData.fatherAddress,
        phone: formData.fatherPhone,
      }));

      // Educational Qualifications - filter out empty/incomplete ones
      const validQualifications = qualifications.filter(q =>
        q.examination && q.universityOrBoard && q.yearOfPassing && q.classOrDivision && q.gradeOrPercentage
      );
      submitData.append('educationalQualifications', JSON.stringify(validQualifications));

      // Employment
      submitData.append('employment', JSON.stringify({
        isEmployed: formData.isEmployed,
        employerName: formData.employerName,
        designation: formData.designation,
        employerAddress: formData.employerAddress,
      }));

      // Declarations
      submitData.append('declarations', JSON.stringify({
        applicantDeclaration: {
          accepted: formData.applicantDeclarationAccepted,
          date: formData.applicantDeclarationDate,
          place: formData.applicantDeclarationPlace,
        },
        parentGuardianDeclaration: {
          accepted: formData.parentDeclarationAccepted,
          date: formData.parentDeclarationDate,
          place: formData.parentDeclarationPlace,
        },
      }));

      // Photo
      if (photoFile) {
        submitData.append('photo', photoFile);
      }

      // Certificate (mandatory)
      if (certificateFile) {
        submitData.append('certificate', certificateFile);
      }

      const response = await studentAPI.create(submitData);
      const studentId = response.data.data?._id || response.data?.student?._id;

      // Create payment record linked to the new student
      if (studentId) {
        await studentPaymentAPI.create({
          studentId,
          paymentType: paymentData.paymentType,
          amount: Number(paymentData.amount),
          transferDate: paymentData.transferDate,
          referenceNumber: paymentData.referenceNumber || undefined,
          fromBank: paymentData.fromBank || undefined,
        });
      }

      toast.success('Student application submitted successfully! Awaiting admin approval.');
      navigate('/admin/students');
    } catch (error) {
      console.error('Error submitting application:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit application';
      // Show inline email error if it's an email duplicate issue
      if (errorMessage.toLowerCase().includes('email')) {
        setEmailError(errorMessage);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Student Registration Form
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/students')}>
          Back to Students
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        BSS Information Technology Mission - Student Application Form. Please fill all required fields carefully.
      </Alert>

      <form onSubmit={handleSubmit}>
        {/* Course Selection */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Course Selection
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Select Course</InputLabel>
                <Select
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  label="Select Course"
                >
                  {courses.map((course) => (
                    <MenuItem key={course._id} value={course._id}>
                      {course.name} ({course.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Batch</InputLabel>
                <Select
                  name="batch"
                  value={formData.batch}
                  onChange={handleChange}
                  label="Batch"
                >
                  {batches.length === 0 ? (
                    <MenuItem disabled value="">
                      No active batches available
                    </MenuItem>
                  ) : (
                    batches.map((b) => (
                      <MenuItem key={b._id} value={b._id}>
                        {b.name} ({b.year})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="BSS Form ID"
                name="bssFormId"
                value={formData.bssFormId}
                onChange={handleChange}
                required
                fullWidth
                helperText="Enter the BSS Form ID as provided"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Personal Information */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Personal Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name (as per TC)"
                name="nameAsPerTC"
                value={formData.nameAsPerTC}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <FormLabel>Gender</FormLabel>
                <RadioGroup
                  row
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Male" control={<Radio />} label="Male" />
                  <FormControlLabel value="Female" control={<Radio />} label="Female" />
                  <FormControlLabel value="Other" control={<Radio />} label="Other" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  handleChange(e);
                  setEmailError('');
                }}
                required
                fullWidth
                error={!!emailError}
                helperText={emailError || 'Required for sending login credentials when approved. Must be unique.'}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Photo Upload */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Student Photo
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Please upload a clear passport-size photo of the student. This will appear on the ID card and records.
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                fullWidth
                color={photoFile ? 'success' : 'primary'}
              >
                {photoFile ? 'Photo Selected' : 'Upload Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Max size: 10MB. Formats: JPG, PNG, GIF (auto-compressed on upload)
              </Typography>
              {photoFile && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  Selected: {photoFile.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {photoPreview && (
                <Card sx={{ maxWidth: 200 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={photoPreview}
                    alt="Student Photo"
                  />
                </Card>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Certificate Upload (Mandatory) */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Certificate Upload <Typography component="span" color="error">*</Typography>
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Please upload a valid educational certificate (e.g., 10th, 12th, Degree). This is mandatory for registration.
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<Description />}
                fullWidth
                color={certificateFile ? 'success' : 'primary'}
              >
                {certificateFile ? 'Certificate Selected' : 'Upload Certificate'}
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={handleCertificateChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Max size: 10MB. Formats: JPG, PNG, GIF, PDF
              </Typography>
              {certificateFile && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  Selected: {certificateFile.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {certificatePreview && (
                <Card sx={{ maxWidth: 200 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={certificatePreview}
                    alt="Certificate Preview"
                  />
                </Card>
              )}
              {certificateFile && !certificatePreview && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  PDF file selected: {certificateFile.name}
                </Alert>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Permanent Address */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Permanent Address
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Street/House Name"
                name="permanentStreet"
                value={formData.permanentStreet}
                onChange={handleChange}
                required
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                name="permanentCity"
                value={formData.permanentCity}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>District</InputLabel>
                <Select
                  label="District"
                  name="permanentDistrict"
                  value={formData.permanentDistrict}
                  onChange={handleChange}
                >
                  {KERALA_DISTRICTS.map((district) => (
                    <MenuItem key={district} value={district}>
                      {district}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="State"
                name="permanentState"
                value={formData.permanentState}
                onChange={handleChange}
                required
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Pincode"
                name="permanentPincode"
                value={formData.permanentPincode}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Communication Address */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Communication Address
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sameAsPermanent}
                  onChange={handleSameAsPermanentChange}
                />
              }
              label="Same as Permanent Address"
            />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Street/House Name"
                name="communicationStreet"
                value={formData.communicationStreet}
                onChange={handleChange}
                required
                fullWidth
                multiline
                rows={2}
                disabled={sameAsPermanent}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                name="communicationCity"
                value={formData.communicationCity}
                onChange={handleChange}
                required
                fullWidth
                disabled={sameAsPermanent}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={sameAsPermanent}>
                <InputLabel>District</InputLabel>
                <Select
                  label="District"
                  name="communicationDistrict"
                  value={formData.communicationDistrict}
                  onChange={handleChange}
                >
                  {KERALA_DISTRICTS.map((district) => (
                    <MenuItem key={district} value={district}>
                      {district}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="State"
                name="communicationState"
                value={formData.communicationState}
                onChange={handleChange}
                required
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Pincode"
                name="communicationPincode"
                value={formData.communicationPincode}
                onChange={handleChange}
                required
                fullWidth
                disabled={sameAsPermanent}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Father/Guardian Information */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Father/Guardian Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Father/Guardian Name"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Occupation"
                name="fatherOccupation"
                value={formData.fatherOccupation}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Address"
                name="fatherAddress"
                value={formData.fatherAddress}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone Number"
                name="fatherPhone"
                value={formData.fatherPhone}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Educational Qualifications */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Educational Qualifications (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add your educational background if applicable
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={addQualification}
            >
              Add Qualification
            </Button>
          </Box>

          {qualifications.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Examination</TableCell>
                    <TableCell>University/Board</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Class/Division</TableCell>
                    <TableCell>Grade/%</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qualifications.map((qual, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.examination}
                          onChange={(e) => handleQualificationChange(index, 'examination', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.universityOrBoard}
                          onChange={(e) => handleQualificationChange(index, 'universityOrBoard', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={qual.yearOfPassing}
                          onChange={(e) => handleQualificationChange(index, 'yearOfPassing', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.classOrDivision}
                          onChange={(e) => handleQualificationChange(index, 'classOrDivision', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.gradeOrPercentage}
                          onChange={(e) => handleQualificationChange(index, 'gradeOrPercentage', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeQualification(index)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No educational qualifications added. Click "Add Qualification" to add your educational background.
            </Alert>
          )}
        </Paper>

        {/* Employment Status */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Employment Status
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isEmployed"
                    checked={formData.isEmployed}
                    onChange={handleChange}
                  />
                }
                label="Currently Employed"
              />
            </Grid>
            {formData.isEmployed && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Employer Name"
                    name="employerName"
                    value={formData.employerName}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Employer Address"
                    name="employerAddress"
                    value={formData.employerAddress}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Declarations */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Declarations
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Applicant's Declaration:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I hereby declare that the information given above is true to the best of my knowledge and belief.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="applicantDeclarationAccepted"
                      checked={formData.applicantDeclarationAccepted}
                      onChange={handleChange}
                      required
                    />
                  }
                  label="I accept the above declaration"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Place"
                  name="applicantDeclarationPlace"
                  value={formData.applicantDeclarationPlace}
                  onChange={handleChange}
                  required={formData.applicantDeclarationAccepted}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Date"
                  name="applicantDeclarationDate"
                  type="date"
                  value={formData.applicantDeclarationDate}
                  onChange={handleChange}
                  required={formData.applicantDeclarationAccepted}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Parent/Guardian's Declaration:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I hereby give my consent for my son/daughter/ward to join this course and declare that the above information is correct.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="parentDeclarationAccepted"
                      checked={formData.parentDeclarationAccepted}
                      onChange={handleChange}
                      required
                    />
                  }
                  label="I accept the above declaration"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Place"
                  name="parentDeclarationPlace"
                  value={formData.parentDeclarationPlace}
                  onChange={handleChange}
                  required={formData.parentDeclarationAccepted}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Date"
                  name="parentDeclarationDate"
                  type="date"
                  value={formData.parentDeclarationDate}
                  onChange={handleChange}
                  required={formData.parentDeclarationAccepted}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Payment Details */}
        <Paper id="payment-section" elevation={2} sx={{ p: 4, mb: 3, border: paymentError ? '1.5px solid #d32f2f' : '1.5px solid transparent' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment Details
            </Typography>
            <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>* Required</Typography>
          </Box>

          {paymentError && (
            <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>
          )}

          {!paymentData.paymentType && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Payment details must be added before submitting the application.
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Payment Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  value={paymentData.paymentType}
                  label="Payment Type"
                  onChange={(e) => setPaymentData((p) => ({ ...p, paymentType: e.target.value, referenceNumber: '', fromBank: '' }))}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Amount Paid (₹)"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData((p) => ({ ...p, amount: e.target.value }))}
                required
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* Transfer / Payment Date */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Date"
                type="date"
                value={paymentData.transferDate}
                onChange={(e) => setPaymentData((p) => ({ ...p, transferDate: e.target.value }))}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* UPI / Card / Bank Transfer — Reference Number */}
            {paymentData.paymentType && paymentData.paymentType !== 'cash' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    paymentData.paymentType === 'upi' ? 'UPI Transaction ID' :
                    paymentData.paymentType === 'card' ? 'Card Reference Number' :
                    'Slip / Reference Number'
                  }
                  value={paymentData.referenceNumber}
                  onChange={(e) => setPaymentData((p) => ({ ...p, referenceNumber: e.target.value }))}
                  required
                  fullWidth
                />
              </Grid>
            )}

            {/* Bank Transfer — From Bank */}
            {paymentData.paymentType === 'bank_transfer' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="From Bank"
                  value={paymentData.fromBank}
                  onChange={(e) => setPaymentData((p) => ({ ...p, fromBank: e.target.value }))}
                  required
                  fullWidth
                  placeholder="e.g. SBI, HDFC, Canara Bank"
                />
              </Grid>
            )}
          </Grid>

          {/* Payment summary chip when complete */}
          {paymentData.paymentType && paymentData.amount && (
            <Box sx={{ mt: 2.5, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                ✓ Payment recorded — ₹{paymentData.amount} via{' '}
                {{ cash: 'Cash', upi: 'UPI', card: 'Card', bank_transfer: 'Bank Transfer' }[paymentData.paymentType]}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/students')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default StudentCreate;
